"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MenuItemBase = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _loggingStateService = require("@smartsheet/logging-state-service");
var _react = _interopRequireWildcard(require("react"));
var _useTheme = require("../../../theme/utils/useTheme");
var _logging = require("../../../util/logging");
var _MenuParentContext = require("../context/MenuParentContext");
var _MenuItemBaseStyled = require("../styled/MenuItemBaseStyled");
const MenuItemBase = /*#__PURE__*/(0, _react.forwardRef)((_ref, parentRef) => {
  let {
    children,
    clientId,
    closeOnSelect = true,
    onClick: providedOnClick,
    onFocus: providedOnFocus,
    onMouseEnter: providedOnMouseEnter,
    onMouseLeave: providedOnMouseLeave,
    isDisabled = false,
    ...rest
  } = _ref;
  const ref = (0, _lodestarUtils.useEnsuredForwardedRef)(parentRef);
  const {
    closeMenuRootRequested,
    closeAllSubMenus,
    focusMode,
    layout,
    allowClickPropagation
  } = (0, _MenuParentContext.useMenuParentContext)();
  const withLogging = (0, _logging.useLoggingDecorator)({
    clientId,
    controlType: "btn"
  });
  const onClickWithLogging = (0, _logging.useLoggingEnabledByDefault)(withLogging, {
    actionId: _loggingStateService.LogActionId.CLICK
  }, providedOnClick);
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const onClick = (0, _react.useCallback)(event => {
    if (!isDisabled) {
      if (!allowClickPropagation) {
        event.stopPropagation();
      }
      void onClickWithLogging(event);
      if (event.defaultPrevented) {
        return;
      }
      if (closeOnSelect) {
        // This won't be null here - it's provided by the MenuParentContext. It's
        // just the typings that need to be a little weird so we can detect the
        // root.
        closeMenuRootRequested?.();
      }
    }
  }, [closeMenuRootRequested, closeOnSelect, isDisabled, onClickWithLogging, allowClickPropagation]);
  const onMouseEnter = (0, _react.useMemo)(() => (0, _lodestarUtils.callAllHandlers)(providedOnMouseEnter, event => {
    if (event.defaultPrevented) {
      return;
    }
    // If the user is navigating with the keyboard, ignore these events.
    // This means the mouse can be hovering over an item and it the menu
    // moves (scrolls, perhaps), the hovered item might get re-focused
    // instead of the one the keyboard would have navigated the user to
    if (focusMode === "keyboard") {
      return;
    }
    // For sub-menu-items, we don't want to do anything more if this item is already expanded.
    if (ref.current?.getAttribute("aria-expanded") === "true") {
      return;
    }
    closeAllSubMenus();
    ref.current?.focus();
  }), [focusMode, closeAllSubMenus, providedOnMouseEnter, ref]);
  const onMouseLeave = (0, _react.useMemo)(() => (0, _lodestarUtils.callAllHandlers)(providedOnMouseLeave, event => {
    if (event.defaultPrevented) {
      return;
    }
    ref.current?.blur();
  }), [providedOnMouseLeave, ref]);
  const onFocus = (0, _react.useMemo)(() => (0, _lodestarUtils.callAllHandlers)(providedOnFocus, () => {
    if (ref.current?.getAttribute("aria-expanded") !== "true") {
      // If the target gets focus again somehow, like if focus leaves the menu for some reason,
      // we'll close the menu to ensure a more consistent experience.
      closeAllSubMenus();
    }
  }), [providedOnFocus, ref, closeAllSubMenus]);
  return /*#__PURE__*/_react.default.createElement(_MenuItemBaseStyled.MenuItemBaseStyled, (0, _extends2.default)({
    role: "menuitem"
  }, rest, {
    isDisabled: isDisabled,
    "aria-disabled": isDisabled || undefined,
    onFocus: onFocus,
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
    // Disallows tab focus on these items.
    // We'll manually manage focus inside the menu w/ the arrow keys.
    // A Tab should close the Menu, and focus whatever is next in line after the Menu's target element.
    // A Shift+Tab should do the same, but for the element previous to the Menu's taret element.
    ,
    tabIndex: -1,
    "data-client-id": clientId,
    ref: ref,
    type: "button",
    layout: layout,
    themeMode: themeMode
  }), children);
});
exports.MenuItemBase = MenuItemBase;
MenuItemBase.displayName = "MenuItem";
//# sourceMappingURL=MenuItemBase.js.map